import { SentryTraced } from "@sentry/nestjs"

import { authUrlForEnvironment, TargetEnv } from "../types/environment.js"
import { AuthResponseSchema, RefreshTokenResponseSchema, Tokens, TokensResponseSchema } from "../types/login.js"

export class AuthService {
  @SentryTraced()
  public async getAuthUrlFromServer(env: TargetEnv, codeChallenge: string) {
    try {
      const {
        result: { authorizeUrl, readKey },
      } = await fetch(authUrlForEnvironment(env), {
        body: JSON.stringify({
          codeChallenge,
        }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      })
        .then(res => res.json())
        .then(async data => AuthResponseSchema.parse(data))
      return { authorizeUrl, readKey }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  @SentryTraced()
  public async getTokensFromServer(env: TargetEnv, verifier: string, readKey: string): Promise<Tokens | undefined> {
    let success = false
    let tokens: Tokens | undefined
    let repeatCounts = 300 // five minutes
    while (!success && repeatCounts-- > 0) {
      success = await fetch(`${authUrlForEnvironment(env)}?read_key=${readKey}&code_verifier=${verifier}`)
        .then(response => {
          if (!response.ok) {
            throw new Error("Network response was not ok")
          }

          return response.json()
        })
        .then(async data => {
          const body = TokensResponseSchema.parse(data)
          if (body.result.status === "Success") {
            tokens = body.result.tokens
            return true
          }

          await this.sleep()

          return false
        })
    }

    return tokens
  }

  @SentryTraced()
  public async refreshToken(env: TargetEnv, refreshToken?: string): Promise<Tokens | undefined> {
    if (!refreshToken) return undefined

    const authUrl = `${authUrlForEnvironment(env)}/token`
    const response = await fetch(authUrl, {
      body: JSON.stringify({
        // eslint-disable-next-line camelcase
        grant_type: "refresh_token",
        // eslint-disable-next-line camelcase
        refresh_token: refreshToken,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })

    if (!response.ok) {
      return undefined
    }

    const responseJson = await response.json()
    const parsedResponse = RefreshTokenResponseSchema.safeParse(responseJson)
    if (!parsedResponse.success) {
      return undefined
    }

    const { tokens } = parsedResponse.data.result
    return { ...tokens, refreshToken }
  }

  @SentryTraced()
  public sleep(ms: number = 5000) {
    return new Promise(resolve => {
      setTimeout(resolve, ms)
    })
  }
}
